import {
  CheckCircleIcon,
  CrossCircleIcon,
  QuestionCircleIcon,
} from "@scribenote/brian-design-system";
import { useState } from "react";

import { candidateEvidence, candidateVerdict } from "./candidateEvidence";
import { byLikelihood, percent, readableEvidence, words } from "./traceFormat";
import type {
  TraceCandidate,
  TraceChoiceCriterion,
  TraceDetail,
  TraceStep,
} from "./types";

type Condition = NonNullable<TraceCandidate["expected_conditions"]>[number];
type LinkedSource = TraceDetail["summary"]["source"];

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

const resultText = (condition: Condition): string => {
  if (condition.type === "rule") {
    if (condition.recorded_result === true) return "True · met";
    if (condition.recorded_result === false) return "False · failed";
    return "Result not recorded";
  }
  if (condition.recorded_choice == null) return "Answer not recorded";
  return `${words(condition.recorded_choice)} · ${condition.recorded_confidence == null ? "confidence not recorded" : percent(condition.recorded_confidence)}`;
};

const barWidth = (fraction: number): string =>
  `${Math.max(0, Math.min(100, fraction * 100))}%`;

const statusText = (condition: Condition): string => {
  switch (condition.status) {
    case "pass":
      return "Condition met";
    case "failed":
      return "Condition failed";
    case "undecided":
      return "Undecided";
    case "unknown":
      return "Result unknown";
  }
};

const statusFill = (condition: Condition): string => {
  switch (condition.status) {
    case "pass":
      return "bg-fill-recorded";
    case "failed":
      return "bg-fill-declined";
    case "undecided":
      return "bg-fill-attention";
    case "unknown":
      return "bg-rule-strong";
  }
};

const ConfidenceBar = ({
  condition,
  label,
}: {
  condition: Condition;
  label?: string;
}) => (
  <div
    className={`relative ${label ? "mt-2" : "mt-1"} h-2 rounded-paper bg-rule`}
    role={label ? "img" : undefined}
    aria-label={label}
    aria-hidden={label ? undefined : true}
  >
    {condition.recorded_confidence != null && (
      <span
        className={`block h-full rounded-paper ${statusFill(condition)}`}
        style={{ width: barWidth(condition.recorded_confidence) }}
      />
    )}
    {condition.min_confidence != null && (
      <span
        className="absolute -top-1 h-4 w-0.5 bg-ink"
        style={{ left: barWidth(condition.min_confidence) }}
      />
    )}
  </div>
);

const AnswerProbabilities = ({ condition }: { condition: Condition }) => {
  const probabilities = byLikelihood(condition.recorded_probabilities ?? {});
  if (probabilities.length === 0) return null;
  return (
    <div className="mt-3">
      <h5 className="text-mono-caps text-xs text-annotation">
        Probability of each answer
      </h5>
      <ul className="mt-1 space-y-1">
        {probabilities.map(([choice, probability]) => {
          const recorded = choice === condition.recorded_choice;
          return (
            <li
              key={choice}
              className="grid grid-cols-[minmax(0,8rem)_minmax(0,1fr)_3.5rem] items-center gap-2 text-xs"
            >
              <span
                className={`break-words ${recorded ? "font-medium text-ink" : "text-ink-body"}`}
              >
                {words(choice)}
                {recorded && (
                  <span className="sr-only"> (recorded answer)</span>
                )}
              </span>
              <span className="h-1.5 rounded-paper bg-rule" aria-hidden="true">
                <span
                  className={`block h-full rounded-paper ${recorded ? statusFill(condition) : "bg-rule-strong"}`}
                  style={{ width: barWidth(probability) }}
                />
              </span>
              <span className="text-right font-mono text-ink-body">
                {percent(probability)}
              </span>
            </li>
          );
        })}
      </ul>
      <p className="mt-1 text-xs text-annotation">
        Jev reports these separately from confidence; only confidence is
        compared with the minimum.
      </p>
    </div>
  );
};

export const AnswerCriteria = ({
  criteria,
  summary,
}: {
  criteria: TraceChoiceCriterion[];
  summary: string;
}) =>
  criteria.length > 0 ? (
    <details className="mt-2 rounded-paper border border-rule bg-card p-3">
      <summary className="cursor-pointer font-medium text-ink">
        {summary}
      </summary>
      <dl className="mt-3 space-y-2">
        {criteria.map((criterion) => (
          <div
            key={criterion.choice}
            className="grid gap-1 sm:grid-cols-[8rem_minmax(0,1fr)]"
          >
            <dt className="break-words font-mono text-xs text-ink-body">
              {criterion.choice}
            </dt>
            <dd className="text-sm text-ink-body">
              {criterion.meaning || "Definition unavailable"}
            </dd>
          </div>
        ))}
      </dl>
    </details>
  ) : null;

const ResultMark = ({ result }: { result: boolean | null | undefined }) => {
  const [Icon, fill] =
    result === true
      ? [CheckCircleIcon, "bg-fill-recorded"]
      : result === false
        ? [CrossCircleIcon, "bg-fill-declined"]
        : [QuestionCircleIcon, "bg-rule"];
  return (
    <span
      className={`inline-flex size-6 shrink-0 items-center justify-center rounded-paper text-ink ${fill}`}
      aria-hidden="true"
    >
      <Icon className="size-4" />
    </span>
  );
};

const RuleResult = ({
  condition,
  partial,
}: {
  condition: Condition;
  partial: boolean;
}) => {
  const result = condition.recorded_result;
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-ink-body">
      <ResultMark result={result} />
      <span>
        {partial
          ? `Saved rule result: ${result === null || result === undefined ? "not recorded" : String(result)}`
          : resultText(condition)}
      </span>
    </span>
  );
};

const CurrentImplementation = ({ condition }: { condition: Condition }) => {
  if (!condition.check_source || !condition.check_expression) return null;
  return (
    <div className="mt-2 rounded-paper border border-rule bg-sheet p-2">
      <p className="text-mono-caps text-xs text-annotation">
        {condition.type === "rule" ? "Rule" : "Question"} implementation in this
        build · <span className="font-mono">{condition.name}</span>
      </p>
      <pre className="mt-1 whitespace-pre-wrap break-all font-mono text-xs text-ink-body">
        {condition.check_expression}
      </pre>
    </div>
  );
};

export const PolicyConditionPreview = ({
  candidate,
}: {
  candidate: TraceCandidate;
}) => {
  const {
    rules,
    modelConditions,
    partial,
    preview,
    fallbackEligible,
    hasFallbackGate,
  } = candidateEvidence(candidate);
  const incomplete = !preview && partial;
  return (
    <div className="space-y-2 p-2" data-dd-privacy="hidden">
      <p className="text-mono-caps text-xs text-annotation">
        {words(candidate.action_type)} · rule checklist
      </p>
      {rules.length > 0 && (
        <ul className="space-y-1">
          {rules.map((rule, index) => {
            const result = rule.recorded_result;
            const resultLabel =
              result === true
                ? "passed"
                : result === false
                  ? "failed"
                  : "not recorded";
            return (
              <li
                key={`${rule.name}-${index}`}
                className="flex min-w-0 items-center gap-2 rounded-paper px-1 py-1 text-sm text-ink"
              >
                <ResultMark result={result} />
                <span className="min-w-0 break-words">{rule.name}</span>
                <span className="sr-only">: {resultLabel}</span>
              </li>
            );
          })}
        </ul>
      )}
      {modelConditions.length > 0 && (
        <ul className="space-y-1 border-t border-rule pt-2">
          {modelConditions.map((condition, index) => {
            return (
              <li
                key={`${condition.name}-${index}`}
                className="min-w-0 rounded-paper px-1 py-1 text-sm text-ink"
              >
                <div className="flex min-w-0 items-center gap-2">
                  <ResultMark
                    result={
                      condition.status === "pass"
                        ? true
                        : condition.status === "failed"
                          ? false
                          : null
                    }
                  />
                  <span className="min-w-0 break-words font-medium">
                    {condition.name}
                  </span>
                  <span className="sr-only">: {statusText(condition)}</span>
                </div>
                <p className="mt-1 text-xs text-ink-body">
                  Actual:{" "}
                  {condition.recorded_choice == null
                    ? "not recorded"
                    : words(condition.recorded_choice)}
                  {" · "}
                  {condition.recorded_confidence == null
                    ? "confidence not recorded"
                    : percent(condition.recorded_confidence)}
                </p>
                <ConfidenceBar condition={condition} />
                <p className="mt-1 text-xs text-annotation">
                  {candidate.expected_conditions === null
                    ? "Saved threshold"
                    : "Current-build minimum"}
                  :{" "}
                  {condition.min_confidence == null
                    ? "not recorded"
                    : percent(condition.min_confidence)}
                  {candidate.expected_conditions !== null &&
                    condition.expected_choice != null && (
                      <>
                        {" "}
                        · Current-build expected:{" "}
                        {words(condition.expected_choice)}
                      </>
                    )}
                </p>
              </li>
            );
          })}
        </ul>
      )}
      {rules.length === 0 && modelConditions.length === 0 && (
        <p className="text-xs text-annotation">
          Rule checklist unavailable for this candidate.
        </p>
      )}
      {hasFallbackGate && (
        <div className="border-t border-rule pt-2">
          <p className="text-mono-caps text-xs text-annotation">
            Fallback eligibility
          </p>
          <p className="mt-1 flex items-center gap-2 text-sm text-ink">
            <ResultMark result={fallbackEligible} />
            Model judgment unavailable
            <span className="sr-only">
              :{" "}
              {fallbackEligible == null
                ? "unknown"
                : fallbackEligible
                  ? "yes"
                  : "no"}
            </span>
          </p>
        </div>
      )}
      {rules.length > 0 && (
        <p className="border-t border-rule pt-2 text-xs text-annotation">
          {candidate.preview_source ===
          "current_policy_definition_with_saved_step_results"
            ? candidate.expected_conditions === null
              ? "Current-build rule list · saved step results. Historical definitions unavailable."
              : "Current-build rule list · saved step results."
            : incomplete
              ? "Saved rule evidence only; full historical list unavailable."
              : "Current-build rule keys · saved results."}
        </p>
      )}
    </div>
  );
};

const SavedStepContext = ({ loaded }: { loaded: TraceStep["loaded"] }) => {
  const entries = Object.entries(asRecord(loaded) ?? {});
  return (
    <div className="rounded-paper border border-rule bg-card p-3">
      <p className="text-mono-caps text-xs text-annotation">
        Saved step context
      </p>
      {entries.length > 0 ? (
        <dl className="mt-2 grid gap-2 sm:grid-cols-2">
          {entries.map(([key, value]) => (
            <div key={key} className="min-w-0">
              <dt className="break-all font-mono text-xs text-annotation">
                {key}
              </dt>
              <dd className="whitespace-pre-wrap break-all font-mono text-xs text-ink-body">
                {value === null ? "None recorded" : readableEvidence(value)}
              </dd>
            </div>
          ))}
        </dl>
      ) : (
        <p className="mt-2 text-xs text-annotation">
          No step context recorded.
        </p>
      )}
      <p className="mt-2 text-xs text-annotation">
        These values were saved with this decision step. Exact evaluated turn
        text and submitted model input were not saved.
      </p>
    </div>
  );
};

const CurrentSource = ({
  source,
  loaded,
}: {
  source: LinkedSource;
  loaded: TraceStep["loaded"];
}) => {
  const savedTurnUuid = asRecord(asRecord(loaded)?.turn)?.uuid;
  if (
    !source?.current ||
    typeof savedTurnUuid !== "string" ||
    source.uuid !== savedTurnUuid ||
    !source.text
  ) {
    return null;
  }
  return (
    <div className="rounded-paper border border-rule bg-card p-3">
      <p className="text-mono-caps text-xs text-annotation">
        Current linked turn
      </p>
      <blockquote className="mt-2 whitespace-pre-wrap break-words border-l-2 border-rule-strong pl-3 text-sm text-ink-body">
        {source.text}
      </blockquote>
      <p className="mt-2 text-xs text-annotation">
        This is current context for the saved turn UUID. It may have changed
        since the decision and is not the exact rule or model input.
      </p>
    </div>
  );
};

const SavedContext = ({
  loaded,
  source,
}: {
  loaded: TraceStep["loaded"];
  source: LinkedSource;
}) => (
  <div className="space-y-2">
    <SavedStepContext loaded={loaded} />
    <CurrentSource source={source} loaded={loaded} />
  </div>
);

const ModelUsed = ({ step }: { step: TraceStep }) => {
  const judgment = asRecord(step.judgment);
  const provider =
    typeof judgment?.provider === "string" ? judgment.provider : null;
  const model = typeof judgment?.model === "string" ? judgment.model : null;
  return (
    <div>
      <h4 className="text-mono-caps text-xs text-annotation">
        Saved model identifiers
      </h4>
      <p className="mt-1 font-mono text-xs">
        {model || "Model not recorded"}
        {provider ? " · Provider: " + provider : " · Provider not recorded"}
      </p>
      <p className="mt-1 text-xs text-annotation">
        {judgment?.called === true
          ? "A call attempt was recorded; that does not establish a usable answer."
          : judgment?.called === false
            ? "No model call was recorded for this step."
            : "Whether a model call was attempted was not recorded."}
      </p>
    </div>
  );
};

const ConditionInspector = ({
  condition,
  loaded,
  source,
  step,
  partial,
  preview,
}: {
  condition: Condition;
  loaded: TraceStep["loaded"];
  source: LinkedSource;
  step: TraceStep;
  partial: boolean;
  preview: boolean;
}) => (
  <div className="space-y-4 pb-4 pl-0 text-sm text-ink-body sm:pl-9">
    {condition.type === "rule" ? (
      <>
        <div>
          <h4 className="text-mono-caps text-xs text-annotation">Check</h4>
          {partial && !preview ? (
            <p className="mt-1">
              Recorded rule key:{" "}
              <span className="font-mono">{condition.name}</span>. Full
              historical rule definition unavailable.
            </p>
          ) : (
            <>
              <p className="mt-1 font-mono">{condition.name}</p>
              {partial && (
                <p className="mt-1 text-xs text-annotation">
                  This rule comes from the policy in this build. The full
                  historical candidate definition is unavailable.
                </p>
              )}
            </>
          )}
        </div>
        <div>
          <h4 className="text-mono-caps text-xs text-annotation">
            Saved result
          </h4>
          <p className="mt-1">
            {condition.recorded_result == null
              ? "Not recorded"
              : condition.recorded_result
                ? "True"
                : "False"}
          </p>
        </div>
        <div>
          <h4 className="text-mono-caps text-xs text-annotation">
            Saved step context
          </h4>
          <div className="mt-2">
            <SavedContext loaded={loaded} source={source} />
          </div>
        </div>
        <div>
          <h4 className="text-mono-caps text-xs text-annotation">Evaluator</h4>
          <p className="mt-1">
            {preview || !partial
              ? "Code rule · no model"
              : "Recorded rule; historical evaluator unavailable"}
          </p>
        </div>
        <CurrentImplementation condition={condition} />
      </>
    ) : (
      <>
        <div>
          <h4 className="text-mono-caps text-xs text-annotation">
            {partial ? "Recorded question key" : "Current-build question"} ·{" "}
            <span className="font-mono">{condition.name}</span>
          </h4>
          <p className="mt-1 whitespace-pre-wrap">
            {partial
              ? "Full historical question instructions not recorded."
              : condition.instructions || "Question instructions unavailable."}
          </p>
          {!partial && condition.expected_choice != null && (
            <p className="mt-1 text-xs text-annotation">
              Current-build candidate requires{" "}
              {words(condition.expected_choice)}.
            </p>
          )}
          <AnswerCriteria
            criteria={condition.criteria}
            summary="Current-build answer meanings"
          />
        </div>
        <div>
          <h4 className="text-mono-caps text-xs text-annotation">
            Input sent to the model
          </h4>
          <p className="mt-1">Exact submitted input: Not recorded.</p>
          <div className="mt-2">
            <SavedContext loaded={loaded} source={source} />
          </div>
        </div>
        <ModelUsed step={step} />
        <div>
          <h4 className="text-mono-caps text-xs text-annotation">
            Recorded answer
          </h4>
          <p className="mt-1">
            {condition.recorded_choice == null
              ? "Answer not recorded"
              : words(condition.recorded_choice)}
            {" · "}
            {condition.recorded_confidence == null
              ? "Confidence not recorded"
              : percent(condition.recorded_confidence)}
          </p>
          <ConfidenceBar
            condition={condition}
            label={
              "Recorded confidence " +
              (condition.recorded_confidence == null
                ? "not recorded"
                : percent(condition.recorded_confidence)) +
              (partial ? "; saved threshold " : "; current-build minimum ") +
              (condition.min_confidence == null
                ? "not recorded"
                : percent(condition.min_confidence)) +
              ". " +
              statusText(condition) +
              "."
            }
          />
          <p className="mt-1 text-xs text-annotation">
            {partial ? "Saved threshold" : "Current-build minimum"}:{" "}
            {condition.min_confidence == null
              ? "Not recorded"
              : percent(condition.min_confidence)}
            {!partial && condition.expected_choice != null && (
              <>
                {" "}
                · Current-build required choice:{" "}
                {words(condition.expected_choice)}
              </>
            )}
          </p>
          <p className="mt-2 text-xs text-annotation">
            {statusText(condition)}.
          </p>
          <AnswerProbabilities condition={condition} />
        </div>
        <CurrentImplementation condition={condition} />
      </>
    )}
  </div>
);

export const PolicyConditions = ({
  candidate,
  step,
  loaded,
  source,
}: {
  candidate: TraceCandidate;
  step: TraceStep;
  loaded: TraceStep["loaded"];
  source: LinkedSource;
}) => {
  const {
    checks,
    partial,
    preview,
    fallbackEligible,
    fallbackNote,
    hasFallbackGate,
  } = candidateEvidence(candidate);
  const firstAttention = checks.findIndex(
    (condition) =>
      condition.status === "failed" || condition.status === "undecided",
  );
  const [openChecks, setOpenChecks] = useState<Set<string>>(() =>
    firstAttention >= 0
      ? new Set([String(firstAttention)])
      : hasFallbackGate && fallbackEligible === false
        ? new Set(["fallback"])
        : new Set(),
  );
  const setCheckOpen = (key: string, open: boolean) => {
    setOpenChecks((current) => {
      if (current.has(key) === open) return current;
      const next = new Set(current);
      if (open) next.add(key);
      else next.delete(key);
      return next;
    });
  };
  const judgment = asRecord(step.judgment);
  return (
    <section
      className="mt-3 border-t border-rule pt-3"
      aria-label="Candidate evidence"
      data-dd-privacy="hidden"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="font-medium text-ink">{words(candidate.action_type)}</h3>
        <p className="text-xs text-annotation">{candidateVerdict(candidate)}</p>
      </div>
      {candidate.selection_note && (
        <p className="mt-2 rounded-paper border border-rule bg-card p-2 text-sm text-ink-body">
          {candidate.selection_note}
        </p>
      )}
      <p className="mt-2 text-xs text-annotation">
        {candidate.definition_source === "policy_definition_in_this_build"
          ? "Condition keys and question configuration come from the matching policy definition in this build; results come from the saved decision."
          : "Full historical policy definition unavailable. Saved candidate evidence is incomplete; other requirements are unknown."}
      </p>
      {preview && (
        <p className="mt-2 text-xs text-annotation">
          The rule list below comes from the policy in this build and is paired
          with saved step results. It is not a saved historical rule list.
        </p>
      )}
      {checks.length > 0 || hasFallbackGate ? (
        <>
          <p className="mt-3 text-mono-caps text-xs text-annotation">
            Requirements
          </p>
          <div className="mt-2">
            {checks.map((condition, index) => (
              <details
                key={condition.type + "-" + condition.name + "-" + index}
                open={openChecks.has(String(index))}
                onToggle={(event) =>
                  setCheckOpen(String(index), event.currentTarget.open)
                }
                className="border-b border-rule"
              >
                <summary className="flex cursor-pointer list-none items-center justify-between gap-3 rounded-paper py-3 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent [&::-webkit-details-marker]:hidden">
                  <span className="min-w-0">
                    <span className="block break-words font-medium text-ink">
                      {condition.name}
                    </span>
                    <span className="mt-1 block text-xs text-annotation">
                      {condition.type === "rule" ? "Rule" : "Model question"} ·{" "}
                      {statusText(condition)}
                    </span>
                  </span>
                  <span className="shrink-0">
                    {condition.type === "rule" ? (
                      <RuleResult condition={condition} partial={partial} />
                    ) : (
                      <span className="text-xs text-ink-body">
                        Actual: {resultText(condition)}
                      </span>
                    )}
                  </span>
                  <span aria-hidden="true" className="text-annotation">
                    ›
                  </span>
                </summary>
                <ConditionInspector
                  condition={condition}
                  loaded={loaded}
                  source={source}
                  step={step}
                  partial={partial}
                  preview={condition.type === "rule" && preview}
                />
              </details>
            ))}
            {hasFallbackGate && (
              <details
                open={openChecks.has("fallback")}
                onToggle={(event) =>
                  setCheckOpen("fallback", event.currentTarget.open)
                }
                className="border-b border-rule"
              >
                <summary className="flex cursor-pointer list-none items-center gap-3 rounded-paper py-3 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent [&::-webkit-details-marker]:hidden">
                  <ResultMark result={fallbackEligible} />
                  <span className="min-w-0 flex-1">
                    <span className="block font-medium text-ink">
                      Model judgment unavailable
                    </span>
                    <span className="mt-1 block text-xs text-annotation">
                      Fallback eligibility ·{" "}
                      {fallbackEligible == null
                        ? "Unknown"
                        : fallbackEligible
                          ? "Met"
                          : "Not met"}
                    </span>
                  </span>
                  <span aria-hidden="true" className="text-annotation">
                    ›
                  </span>
                </summary>
                <div className="space-y-3 pb-4 pl-0 text-sm text-ink-body sm:pl-9">
                  <p>
                    {fallbackNote ||
                      "The saved judgment does not establish this fallback gate."}
                  </p>
                  <p>
                    Saved escalation reason:{" "}
                    <span className="font-mono">
                      {typeof judgment?.escalation_reason === "string"
                        ? judgment.escalation_reason
                        : judgment?.escalation_reason === null
                          ? "None recorded"
                          : "Not recorded"}
                    </span>
                  </p>
                  <p>
                    Model call attempted:{" "}
                    {judgment?.called === true
                      ? "Yes"
                      : judgment?.called === false
                        ? "No"
                        : "Not recorded"}
                    . A call attempt does not establish a usable response.
                  </p>
                  <ModelUsed step={step} />
                </div>
              </details>
            )}
          </div>
          <p className="mt-3 text-xs text-annotation">
            All requirements must pass for this candidate to qualify.
          </p>
        </>
      ) : (
        <div className="mt-3 text-sm text-ink-body">
          <p>Condition definitions are not recorded for this evaluation.</p>
          {candidate.failed_rule && (
            <p className="mt-1">
              Recorded failed condition: {candidate.failed_rule}
            </p>
          )}
          {candidate.question && (
            <p className="mt-1">
              Recorded model question: {candidate.question}
            </p>
          )}
          {candidate.choice !== null && (
            <p className="mt-1">
              Recorded answer: {candidate.choice}
              {candidate.confidence != null
                ? " · " + percent(candidate.confidence) + " confidence"
                : ""}
            </p>
          )}
          <SavedContext loaded={loaded} source={source} />
        </div>
      )}
      <details className="mt-3 text-xs text-ink-body">
        <summary className="cursor-pointer">Raw recorded evaluation</summary>
        <div className="mt-2 grid gap-2 sm:grid-cols-2">
          <pre className="max-h-48 overflow-auto whitespace-pre-wrap break-all rounded-paper border border-rule bg-sheet p-2 font-mono">
            Rules: {readableEvidence(step.rules)}
          </pre>
          <pre className="max-h-48 overflow-auto whitespace-pre-wrap break-all rounded-paper border border-rule bg-sheet p-2 font-mono">
            Model result: {readableEvidence(step.judgment)}
          </pre>
        </div>
      </details>
    </section>
  );
};
